import axios from 'axios'
import request from '@api/helpers/authorizateRequest'

const BASE_URL = '/registrations/recovery'
const ENDPOINTS = {
    workspace: `${BASE_URL}/workspace`,
    intake: `${BASE_URL}/intake`,
    legacy: `${BASE_URL}/legacy`,
    operators: `${BASE_URL}/operators`,
    reminders: `${BASE_URL}/reminders`,
    statistics: `${BASE_URL}/statistics`,
    config: `${BASE_URL}/config`,
    models: `${BASE_URL}/models`,
    process: (id) => `${BASE_URL}/${encodeURIComponent(id)}`,
    action: (id, action) => `${BASE_URL}/${encodeURIComponent(id)}/${action}`,
}

function object(value) {
    return value && typeof value === 'object' && !Array.isArray(value)
}

function invalidResponse() {
    return new Error('Сервер вернул неполные данные регистрации. Обновите страницу.')
}

function process(value, id) {
    if (object(value)) {
        // PHP serializes an empty decoded object as an empty array.
        if (Array.isArray(value.fields) && !value.fields.length) value.fields = {}
        if (Array.isArray(value.vehicle) && !value.vehicle.length) value.vehicle = {}
    }
    if (!object(value) || !value.id || !value.company_id || !object(value.fields) || !object(value.vehicle)) {
        throw invalidResponse()
    }
    if (id != null && String(value.id) !== String(id)) throw invalidResponse()
    return value
}

async function get(url, params = {}, cancelToken = null) {
    return (await request.get(url, { params, cancelToken })).data.response
}

async function post(id, action, payload, cancelToken = null) {
    return (await request.post(ENDPOINTS.action(id, action), payload, { cancelToken })).data.response
}

function dispatchState(value) {
    if (!object(value) || !Array.isArray(value.operations) || !object(value.check)) throw invalidResponse()
    if (Array.isArray(value.check.errors) && !value.check.errors.length) value.check.errors = {}
    if (
        !object(value.check.errors) ||
        !Array.isArray(value.check.duplicates) ||
        !Array.isArray(value.check.vehicles)
    ) {
        throw invalidResponse()
    }
    return value
}

export default {
    async workspace(cancelToken) {
        const result = await get(ENDPOINTS.workspace, {}, cancelToken)
        if (!object(result) || !Array.isArray(result.companies) || !Array.isArray(result.channels))
            throw invalidResponse()
        if (
            result.companies.some((company) => !object(company) || !company.company_id || !object(company.features)) ||
            result.channels.some((channel) => !object(channel) || !channel.company_id || typeof channel.key !== 'string')
        ) {
            throw invalidResponse()
        }
        return result
    },
    async intake(payload, cancelToken) {
        return process((await request.post(ENDPOINTS.intake, payload, { cancelToken })).data.response)
    },
    legacy(search, cancelToken, companyId) {
        return get(ENDPOINTS.legacy, { search, company_id: companyId }, cancelToken)
    },
    operators(id, cancelToken) {
        return get(ENDPOINTS.operators, { id }, cancelToken)
    },
    reminders(cancelToken) {
        return get(ENDPOINTS.reminders, { workspace: 1 }, cancelToken)
    },
    statistics(params, cancelToken) {
        return get(ENDPOINTS.statistics, { ...params, workspace: 1 }, cancelToken)
    },
    async conversation(id, before, cancelToken) {
        const result = await get(ENDPOINTS.action(id, 'conversation'), before ? { before } : {}, cancelToken)
        if (!object(result) || !Array.isArray(result.messages)) throw invalidResponse()
        return result
    },
    cancelSource: () => axios.CancelToken.source(),
    isCancelled: (error) => axios.isCancel(error),
    async list(params, cancelToken) {
        const result = await get(BASE_URL, { ...params, workspace: 1 }, cancelToken)
        if (!object(result) || !Array.isArray(result.items)) throw invalidResponse()
        result.items.forEach((item) => process(item))
        return result
    },
    async show(id, cancelToken) {
        return process(await get(ENDPOINTS.process(id), {}, cancelToken), id)
    },
    async act(id, payload, cancelToken) {
        return process(await post(id, 'actions', payload, cancelToken), id)
    },
    async config(cancelToken, companyId) {
        const result = await get(ENDPOINTS.config, { company_id: companyId }, cancelToken)
        if (!object(result) || !result.company_id) throw invalidResponse()
        return result
    },
    async models(brand, cancelToken, companyId) {
        const result = await get(ENDPOINTS.models, { brand, company_id: companyId }, cancelToken)
        if (!Array.isArray(result)) throw invalidResponse()
        return result
    },
    async inspect(id, payload, cancelToken) {
        const result = await post(id, 'inspect', payload, cancelToken)
        if (
            !object(result) ||
            !object(result.check) ||
            !Array.isArray(result.vehicles) ||
            !Array.isArray(result.name_matches)
        )
            throw invalidResponse()
        if (Array.isArray(result.check.errors) && !result.check.errors.length) result.check.errors = {}
        return result
    },
    async dispatchView(id, cancelToken) {
        return dispatchState(await get(ENDPOINTS.action(id, 'dispatch'), {}, cancelToken))
    },
    async prepare(id, version, cancelToken) {
        return dispatchState(await post(id, 'prepare', { version }, cancelToken))
    },
    async dispatch(id, version, requestId, cancelToken) {
        return dispatchState(await post(id, 'dispatch', { version, request_id: requestId }, cancelToken))
    },
    async adopt(id, payload, cancelToken) {
        return dispatchState(await post(id, 'adopt', payload, cancelToken))
    },
    async confirmAbsent(id, payload, cancelToken) {
        return dispatchState(await post(id, 'confirm-absent', payload, cancelToken))
    },
    async reconcile(id, payload, cancelToken) {
        return dispatchState(await post(id, 'reconcile', payload, cancelToken))
    },
    async vehicle(id, carId, cancelToken) {
        const result = await get(ENDPOINTS.action(id, 'vehicle'), { car_id: carId }, cancelToken)
        if (!object(result) || !object(result.vehicle) || !Array.isArray(result.locked_fields))
            throw invalidResponse()
        return result
    },
    vehicleAction(id, payload, cancelToken) {
        return post(id, 'vehicle', payload, cancelToken)
    },
    vehicleReconcile(id, version, cancelToken) {
        return post(id, 'vehicle-reconcile', { version }, cancelToken)
    },
}
